import { analyticsService } from '../index-business';

export interface PendingClientEventData {
  ownerId?: string;
  funnelId?: string;
  pendingClientId?: string;
  studioClientId?: string;
  leadData?: any;
  saleData?: any;
  [key: string]: any;
}

export class PendingClientTracker {

  static trackAddClient(data: {
    ownerId: string;
    funnelId: string;
    leadData: any;
    success: boolean;
    error?: string;
    duration?: number;
  }) {
    const eventData = {
      owner_id: data.ownerId,
      funnel_id: data.funnelId,
      lead_name: data.leadData?.name,
      lead_phone: data.leadData?.phone ? 'has_phone' : 'no_phone',
      lead_email: data.leadData?.email ? 'has_email' : 'no_email',
      lead_source: data.leadData?.source,
      lead_tags: data.leadData?.tags || [],
      lead_stage: data.leadData?.stage || 0,
      has_notes: data.leadData?.notes ? true : false,
      notes_length: data.leadData?.notes?.length || 0,
      has_extra_fields: data.leadData?.extraFields ? Object.keys(data.leadData.extraFields).length > 0 : false,
      extra_fields_count: data.leadData?.extraFields ? Object.keys(data.leadData.extraFields).length : 0,
      success: data.success,
      error_message: data.error,
      api_duration_ms: data.duration,
      event_type: 'api_call',
      api_endpoint: 'addClient',
      timestamp: new Date().toISOString()
    };

    analyticsService.track('Lead Added', eventData);
  }

  static trackUpdateClient(data: {
    ownerId: string;
    funnelId: string;
    id: string;
    leadData: any;
    success: boolean;
    error?: string;
    duration?: number;
  }) {
    const updatedFields = data.leadData ? Object.keys(data.leadData) : [];

    const eventData = {
      owner_id: data.ownerId,
      funnel_id: data.funnelId,
      pending_client_id: data.id,
      updated_fields: updatedFields,
      lead_name: data.leadData?.name,
      lead_phone: data.leadData?.phone ? 'has_phone' : 'no_phone',
      lead_email: data.leadData?.email ? 'has_email' : 'no_email',
      lead_source: data.leadData?.source,
      lead_tags: data.leadData?.tags || [],
      lead_stage: data.leadData?.stage,
      stage_changed: updatedFields.includes('stage'),
      has_notes: data.leadData?.notes ? true : false,
      notes_length: data.leadData?.notes?.length || 0,
      has_extra_fields: data.leadData?.extraFields ? Object.keys(data.leadData.extraFields).length > 0 : false,
      extra_fields_count: data.leadData?.extraFields ? Object.keys(data.leadData.extraFields).length : 0,
      success: data.success,
      error_message: data.error,
      api_duration_ms: data.duration,
      event_type: 'api_call',
      api_endpoint: 'updateClient',
      timestamp: new Date().toISOString()
    };

    analyticsService.track('Lead Updated', eventData);
  }

  static trackAddStudioClient(data: {
    ownerId: string;
    funnelId: string;
    studioClientId: string;
    leadData: any;
    success: boolean;
    error?: string;
    duration?: number;
  }) {
    const eventData = {
      owner_id: data.ownerId,
      funnel_id: data.funnelId,
      studio_client_id: data.studioClientId,
      lead_source: data.leadData?.source,
      lead_tags: data.leadData?.tags || [],
      lead_stage: data.leadData?.stage || 0,
      has_notes: data.leadData?.notes ? true : false,
      conversion_type: 'existing_client_to_lead',
      success: data.success,
      error_message: data.error,
      api_duration_ms: data.duration,
      event_type: 'api_call',
      api_endpoint: 'addStudioClient',
      timestamp: new Date().toISOString()
    };

    analyticsService.track('Existing Client Added to Funnel', eventData);
  }

  static trackDeleteClient(data: {
    ownerId: string;
    id: string;
    success: boolean;
    error?: string;
    duration?: number;
  }) {
    const eventData = {
      owner_id: data.ownerId,
      pending_client_id: data.id,
      action_type: 'delete_lead',
      success: data.success,
      error_message: data.error,
      api_duration_ms: data.duration,
      event_type: 'api_call',
      api_endpoint: 'deleteClient',
      timestamp: new Date().toISOString()
    };

    analyticsService.track('Lead Deleted', eventData);
  }

  static trackAddSaleHistory(data: {
    ownerId: string;
    funnelId: string;
    pendingClientId: string;
    saleData: any;
    success: boolean;
    error?: string;
    duration?: number;
  }) {
    const eventData = {
      owner_id: data.ownerId,
      funnel_id: data.funnelId,
      pending_client_id: data.pendingClientId,
      sale_action: data.saleData?.action,
      sale_status: data.saleData?.status,
      sale_stage: data.saleData?.stage,
      sale_amount: data.saleData?.amount,
      sale_currency: data.saleData?.currency,
      employee_id: data.saleData?.employeeId,
      has_notes: data.saleData?.notes ? true : false,
      notes_length: data.saleData?.notes?.length || 0,
      sale_date: data.saleData?.date || new Date().toISOString(),
      success: data.success,
      error_message: data.error,
      api_duration_ms: data.duration,
      event_type: 'api_call',
      api_endpoint: 'addSaleHistory',
      timestamp: new Date().toISOString()
    };

    analyticsService.track('Sale History Added', eventData);
  }

  static trackAddMultipleSaleHistory(data: {
    ownerId: string;
    funnelId: string;
    saleData: any;
    success: boolean;
    error?: string;
    duration?: number;
    salesCount?: number;
  }) {
    const eventData = {
      owner_id: data.ownerId,
      funnel_id: data.funnelId,
      sales_count: data.salesCount || data.saleData?.sales?.length || 0,
      batch_operation: true,
      total_amount: data.saleData?.sales?.reduce((sum: number, sale: any) => sum + (sale.amount || 0), 0) || 0,
      currencies_used: data.saleData?.sales?.map((sale: any) => sale.currency).filter((c: any) => c) || [],
      employees_involved: data.saleData?.sales?.map((sale: any) => sale.employeeId).filter((e: any) => e) || [],
      success: data.success,
      error_message: data.error,
      api_duration_ms: data.duration,
      event_type: 'api_call',
      api_endpoint: 'addMultipleSaleHistory',
      timestamp: new Date().toISOString()
    };

    analyticsService.track('Multiple Sale History Added', eventData);
  }

  static trackAddSaleStatus(data: {
    ownerId: string;
    funnelId: string;
    pendingClientId: string;
    statusData: any;
    success: boolean;
    error?: string;
    duration?: number;
  }) {
    const eventData = {
      owner_id: data.ownerId,
      funnel_id: data.funnelId,
      pending_client_id: data.pendingClientId,
      new_status: data.statusData?.status,
      new_stage: data.statusData?.stage,
      status_reason: data.statusData?.reason,
      employee_id: data.statusData?.employeeId,
      has_notes: data.statusData?.notes ? true : false,
      notes_length: data.statusData?.notes?.length || 0,
      status_date: data.statusData?.date || new Date().toISOString(),
      success: data.success,
      error_message: data.error,
      api_duration_ms: data.duration,
      event_type: 'api_call',
      api_endpoint: 'addSaleStatus',
      timestamp: new Date().toISOString()
    };

    analyticsService.track('Sale Status Added', eventData);
  }

  static trackAddMultipleSaleStatus(data: {
    ownerId: string;
    funnelId: string;
    statusData: any;
    success: boolean;
    error?: string;
    duration?: number;
    statusesCount?: number;
  }) {
    const eventData = {
      owner_id: data.ownerId,
      funnel_id: data.funnelId,
      statuses_count: data.statusesCount || data.statusData?.statuses?.length || 0,
      batch_operation: true,
      statuses_types: data.statusData?.statuses?.map((status: any) => status.status).filter((s: any) => s) || [],
      employees_involved: data.statusData?.statuses?.map((status: any) => status.employeeId).filter((e: any) => e) || [],
      success: data.success,
      error_message: data.error,
      api_duration_ms: data.duration,
      event_type: 'api_call',
      api_endpoint: 'addMultipleSaleStatus',
      timestamp: new Date().toISOString()
    };

    analyticsService.track('Multiple Sale Status Added', eventData);
  }

  static trackPendingClientApiCall(endpoint: string, data: PendingClientEventData & {
    success: boolean;
    error?: string;
    duration?: number;
  }) {
    const eventData = {
      ...data,
      event_type: 'api_call',
      api_endpoint: endpoint,
      timestamp: new Date().toISOString()
    };

    analyticsService.track(`Pending Client API: ${endpoint}`, eventData);
  }
}

export const trackPendingClientSuccess = (endpoint: string, data: PendingClientEventData, duration?: number) => {
  PendingClientTracker.trackPendingClientApiCall(endpoint, {
    ...data,
    success: true,
    duration
  });
};

export const trackPendingClientError = (endpoint: string, error: string, data: PendingClientEventData, duration?: number) => {
  PendingClientTracker.trackPendingClientApiCall(endpoint, {
    ...data,
    success: false,
    error,
    duration
  });
};

export default PendingClientTracker;
